import { useAuth } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { getWaitlist, type WaitlistPosition } from "../lib/waitlist-api";
import { useReadinessStore } from "../stores/readiness";

export interface UseWaitlistResult {
  waitlist: WaitlistPosition | null;
  refresh: () => Promise<void>;
}

/**
 * Owner-scoped geo-waitlist position read (M11-C2). Returns null when the caller
 * is not on any queue (their target area IS a launched active metro). Soft/nil-safe:
 * a fetch error leaves the card hidden. `refresh` re-reads after a mutation (leave).
 *
 * Reactivity to target changes (operator 2026-07-23): the position is a function of
 * the buyer's ACTIVE readiness target area — moving Seattle→Austin flips them from
 * launched to queued and vice-versa. We re-fetch whenever the shared readiness store's
 * active `target.id` changes. A target edit (`PUT /api/readiness/target`) supersedes
 * the prior target with a NEW id and refreshes that store, so the card switches state
 * the instant the target is saved instead of going stale until a manual reload. No
 * dedicated waitlist store is needed — the readiness store is the one every target
 * mutation already bumps (ADR-027 mutation surfaces).
 *
 * The tokenRef holds Clerk's unstable `getToken` so the fetch effect's deps don't
 * re-fetch every render (the useMarketplaceConnection precedent). `genRef` makes the
 * latest request win, so a Seattle→Austin switch never lands a stale in-flight read.
 */
export interface UseWaitlistOptions {
  // SILL-504 single launch gate: surfaces OUTSIDE MarketplaceRouteGate (the
  // dashboard passport) pass the marketplace_go_live_enabled flag here so a
  // flag-off render never fetches nor surfaces waitlist state. Callers already
  // inside the gate omit it (defaults true).
  enabled?: boolean;
}

export function useWaitlist(
  options: UseWaitlistOptions = {},
): UseWaitlistResult {
  const enabled = options.enabled ?? true;
  const { getToken } = useAuth();
  const [waitlist, setWaitlist] = useState<WaitlistPosition | null>(null);
  const tokenRef = useRef(getToken);
  const activeRef = useRef(true);
  const genRef = useRef(0);
  const targetId = useReadinessStore((s) => s.data?.target?.id ?? null);

  useEffect(() => {
    tokenRef.current = getToken;
  }, [getToken]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const gen = ++genRef.current;
    try {
      const result = await getWaitlist(() => tokenRef.current());
      // Drop the write if a newer refresh started (target switched mid-flight)
      // or the hook unmounted.
      if (activeRef.current && gen === genRef.current) setWaitlist(result);
    } catch {
      if (activeRef.current && gen === genRef.current) setWaitlist(null);
    }
  }, [enabled]);

  useEffect(() => {
    activeRef.current = true;
    if (enabled) void refresh();
    return () => {
      activeRef.current = false;
    };
  }, [refresh, targetId, enabled]);

  // Flag-off (or flipped off mid-session): surface null so the card and the
  // publish-block relabel disappear together; any stale internal position is
  // overwritten by the refresh that fires when the flag flips back on.
  return { waitlist: enabled ? waitlist : null, refresh };
}
